import { Link } from 'react-router-dom';
import { ArrowRight, Droplets, Sparkles, Shield, Car, Wrench, Crown } from 'lucide-react';

const DETAILING_IMAGE = "/images/detailing.jpg";
const INTERIOR_IMAGE = "/images/interior.jpg";

const Services = () => {
  const premiumServices = [
    {
      icon: Droplets,
      title: 'Exterior Hand Wash',
      description: 'Gentle foam wash with safe drying techniques to protect your car\'s paint.',
    },
    {
      icon: Car,
      title: 'Interior Vacuuming',
      description: 'Thorough cleaning of seats, floor mats, and all interior surfaces.',
    },
    {
      icon: Sparkles,
      title: 'Wheel & Tire Cleaning',
      description: 'Deep cleaning and polishing for sparkling wheels and shiny tires.',
    },
    {
      icon: Shield,
      title: 'Glass Polishing',
      description: 'Streak-free finish for all windows and mirrors for crystal clear visibility.',
    },
  ];

  const advancedServices = [
    {
      icon: Sparkles,
      title: 'Premium Waxing',
      description: 'Protective coating that gives your car a showroom shine.',
    },
    {
      icon: Car,
      title: 'Leather Conditioning',
      description: 'Nourishing treatment for leather and upholstery surfaces.',
    },
    {
      icon: Wrench,
      title: 'Engine Bay Cleaning',
      description: 'Safe and thorough cleaning of your engine compartment.',
    },
    {
      icon: Droplets,
      title: 'Stain & Odor Treatment',
      description: 'Professional removal of stubborn stains and unwanted odors.',
    },
  ];

  const membershipBenefits = [
    'Monthly packages for regular maintenance',
    'VIP memberships for exclusive benefits',
    'Priority scheduling and service',
    'Special discounts and offers',
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-[#1E88E5]/10 rounded-full text-[#1E88E5] font-medium text-sm mb-6">
                Our Services
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0B2C5D] mb-6">
                Services We Offer
              </h1>
              <p className="text-lg text-[#64748B] mb-8 leading-relaxed">
                From quick exterior rinses to comprehensive detailing, we provide a full range of premium car care services delivered right to your doorstep.
              </p>
              <Link
                to="/service-offer-plan"
                className="inline-flex items-center bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg hover:shadow-orange-500/30"
                data-testid="services-view-pricing"
              >
                View Pricing <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
            <div className="relative">
              <img 
                src={DETAILING_IMAGE}
                alt="Professional Car Detailing"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Premium Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-4">
              Premium Car Wash Services
            </h2>
            <p className="text-[#64748B] max-w-2xl mx-auto">
              Our core services that keep your car looking pristine.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {premiumServices.map((service, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1"
                data-testid={`premium-service-${index}`}
              >
                <div className="w-14 h-14 bg-[#1E88E5]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#1E88E5] transition-colors">
                  <service.icon className="h-7 w-7 text-[#1E88E5] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#0B2C5D] mb-3">
                  {service.title}
                </h3>
                <p className="text-[#64748B]">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advanced Services */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src={INTERIOR_IMAGE}
                alt="Car Interior Cleaning"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-8">
                Advanced Auto Care Services
              </h2>
              <div className="space-y-6">
                {advancedServices.map((service, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all"
                    data-testid={`advanced-service-${index}`}
                  >
                    <div className="w-12 h-12 bg-[#F7931E]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <service.icon className="h-6 w-6 text-[#F7931E]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-[#0B2C5D] mb-1">
                        {service.title}
                      </h3>
                      <p className="text-[#64748B] text-sm">{service.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Membership */}
      <section className="py-20 bg-[#0B2C5D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="w-16 h-16 bg-[#F7931E]/20 rounded-2xl flex items-center justify-center mb-6">
                <Crown className="h-8 w-8 text-[#F7931E]" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Subscription & Membership Plans
              </h2>
              <p className="text-white/80 mb-8 leading-relaxed">
                Save more with our subscription plans. Perfect for car enthusiasts who want regular maintenance without the hassle.
              </p>
              <ul className="space-y-4 mb-8">
                {membershipBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-center text-white">
                    <div className="w-2 h-2 bg-[#F7931E] rounded-full mr-3"></div>
                    {benefit}
                  </li>
                ))}
              </ul>
              <Link
                to="/service-offer-plan"
                className="inline-flex items-center bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg"
                data-testid="services-membership"
              >
                View All Plans <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-3xl p-8">
              <h3 className="font-heading text-2xl font-bold text-white mb-6">
                VIP Membership Benefits
              </h3>
              <ul className="space-y-4">
                {[
                  'No Booking Required',
                  '15-Minute Response Guarantee',
                  'On-Demand Doorstep Service',
                  'Top Priority Service',
                  'Zero Waiting Time',
                  'Dedicated VIP Service Team',
                ].map((item, index) => (
                  <li key={index} className="flex items-center text-white/90">
                    <svg className="w-5 h-5 text-[#F7931E] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-6">
            Ready for Premium Car Care?
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto mb-8">
            Book your service today and experience the Washora difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/booking"
              className="bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg hover:shadow-orange-500/30 flex items-center justify-center"
              data-testid="services-book-now"
            >
              Book Now <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a
              href="https://wa.me/919044499957?text=Hi%20Washora,%20I'd%20like%20to%20know%20more%20about%20your%20services."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#0B2C5D] border-2 border-[#0B2C5D] px-8 py-4 rounded-full font-semibold hover:bg-[#0B2C5D] hover:text-white transition-all flex items-center justify-center"
            >
              Ask on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
